import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { OpUser } from '../model/a2srv-operation/auth-models/opuser-model';
import { UrlPrefixes } from '../model/cms-btv-core/config-controller/config-url-prefix.model';
import { ProgramP } from '../model/cms-btv-core/programP.model';
import { Catchup } from '../model/cms-vod-core/catchup-models/catchup-model';
import { ContentCast } from '../model/content-cast.model';
import { ContentPoster } from '../model/content-poster.model';
import { Content } from '../model/content.model';
import { License } from '../model/license.model';
import { QueryParams } from '../model/query-models/query-models';
import { UtilityModel } from '../model/utility-models/utility.model';
import { VodQueryParams } from '../model/vod-query-params.model';
import { Channel } from '../model/cms-btv-core/channel.model';
import { CustomerQueryParams } from '../model/customer-query-params.model';

@Injectable({
  providedIn: 'root',
})
export class DataSharingService {
  currentUser: OpUser = new OpUser();
  private posters: Array<ContentPoster> = new Array<ContentPoster>();
  catchupPoster: ContentPoster[] = [];
  license$: License = new License();
  urlPrefix: UrlPrefixes = {};
  content$: Content = {};
  location: string;

  private epgProgramId = new Subject<string>();
  private processDataSource = new Subject<string>();
  private dataSource: Subject<boolean> = new Subject<boolean>();
  private userDataSource: Subject<OpUser> = new BehaviorSubject(this.currentUser);
  private refreshStatus: Subject<boolean> = new Subject<boolean>();
  private roleAdded = new Subject<boolean>();

  private contentPosters: BehaviorSubject<Array<ContentPoster>> = new BehaviorSubject<Array<ContentPoster>>(new Array<ContentPoster>());

  private provider = new Subject<boolean>();
  private license = new BehaviorSubject<License>(this.license$);
  private utilityModal = new BehaviorSubject<UtilityModel>(new UtilityModel());
  private content = new BehaviorSubject<Content>(new Content());
  private locationPrefix: Subject<string> = new BehaviorSubject<string>(null);
  private urlPrefixes = new BehaviorSubject<UrlPrefixes>(new UrlPrefixes());
  private contentUrlPrefixes = new BehaviorSubject<UrlPrefixes>(new UrlPrefixes());
  private categoryUrlPrefixes = new BehaviorSubject<UrlPrefixes>(new UrlPrefixes());
  private menuItemUrlPrefixes = new BehaviorSubject<UrlPrefixes>(new UrlPrefixes());
  private castUrlPrefixes = new BehaviorSubject<UrlPrefixes>(new UrlPrefixes());
  private catchupUrlPrefixes = new BehaviorSubject<UrlPrefixes>(new UrlPrefixes());
  private contractUrlPrefix = new BehaviorSubject<UrlPrefixes>(new UrlPrefixes());
  private epgUrlPrefix: Subject<UrlPrefixes> = new BehaviorSubject<UrlPrefixes>(new UrlPrefixes());
  private cdnFilePrefix: Subject<UrlPrefixes> = new BehaviorSubject<UrlPrefixes>(new UrlPrefixes());
  private comingSoonUrlPrefixes = new BehaviorSubject<UrlPrefixes>(new UrlPrefixes());
  private providerUrlPrefixes = new BehaviorSubject<UrlPrefixes>(new UrlPrefixes());
  private reportUrlPrefix: BehaviorSubject<UrlPrefixes> = new BehaviorSubject<UrlPrefixes>(new UrlPrefixes());
  private queryParam = new BehaviorSubject<VodQueryParams>(new VodQueryParams());
  private castUpdate: Subject<ContentCast> = new BehaviorSubject<ContentCast>(new ContentCast());
  private mediaType: Subject<string> = new BehaviorSubject<string>(null);
  private operationModalName: BehaviorSubject<UtilityModel> = new BehaviorSubject<UtilityModel>(new UtilityModel());
  private catchup: BehaviorSubject<Catchup> = new BehaviorSubject<Catchup>(new Catchup());
  private catchupPosters: BehaviorSubject<ContentPoster[]> = new BehaviorSubject<ContentPoster[]>([]);
  // Program poster definition IDs that are auto-matched (used by poster modal to show green)
  programPosterDefinitionIds: number[] = [];
  // private program: BehaviorSubject<EpgProgramInsertData> = new BehaviorSubject<EpgProgramInsertData>(new EpgProgramInsertData());
  private program: Subject<ProgramP> = new BehaviorSubject<ProgramP>(new ProgramP());
  private queryParams: BehaviorSubject<QueryParams> = new BehaviorSubject<QueryParams>(new QueryParams());
  private bulkOperationContents: Subject<any[]> = new BehaviorSubject<any[]>([]);

  private selectedChannel: Subject<Channel> = new BehaviorSubject<Channel>(new Channel());
  private clearBulkContents: Subject<boolean> = new BehaviorSubject<boolean>(false);
  private crmQueryParam = new BehaviorSubject<CustomerQueryParams>(new CustomerQueryParams());
  private channelAndDate = new BehaviorSubject<{ channelId: number, releaseDate: string }>({ channelId: null, releaseDate: null });
  private contentName = new BehaviorSubject<string>(localStorage.getItem('contentName'));
  private refreshSubject = new Subject<boolean>();

  channelAndDate$ = this.channelAndDate.asObservable();
  dataSource$ = this.dataSource.asObservable();
  userDataSource$ = this.userDataSource.asObservable();
  roleAdded$ = this.roleAdded.asObservable();
  provider$ = this.provider.asObservable();

  setBulkOperationContents(bulkOperationContents: any[]): void {
    if (bulkOperationContents) {
      this.bulkOperationContents.next(bulkOperationContents);
    }
  }

  getBulkOperationContents(): Observable<any[]> {
    return this.bulkOperationContents.asObservable();
  }

  setSelectedChannel(selectedChannel: Channel): void {
    this.selectedChannel.next(selectedChannel);
  }

  getSelectedChannel(): Observable<Channel> {
    return this.selectedChannel.asObservable();
  }

  sendChannelAndDateData(data: { channelId: number, releaseDate: string }) {
    this.channelAndDate.next(data);
  }

  getEpgProgram(): Observable<ProgramP> {
    return this.program.asObservable();
  }

  getContentName(): Observable<string> {
    return this.contentName.asObservable();
  }

  setRefresh(status: boolean): void {
    this.refreshStatus.next(status);
  }

  getRefresh(): Observable<boolean> {
    return this.refreshStatus.asObservable();
  }

  setQueryParam(queryParam: QueryParams): void {
    this.queryParams.next(queryParam);
  }

  getQueryParam(): Observable<QueryParams> {
    return this.queryParams.asObservable();
  }

  getEpgProgramId(): Observable<string> {
    return this.epgProgramId.asObservable();
  }

  sendProcessData(data: string) {
    this.processDataSource.next(data);
  }

  sendData(data: boolean) {
    this.dataSource.next(data);
  }

  sendUserData(data: OpUser) {
    this.userDataSource.next(data);
  }

  getUserData(): Observable<OpUser> {
    if (!this.currentUser.username) {
      this.currentUser.username = localStorage.getItem('username');
      this.currentUser.token = localStorage.getItem('token');
      this.currentUser.id = parseInt(localStorage.getItem('userID'));
      this.currentUser.opuiUrls = JSON.parse(localStorage.getItem('urls'));
    }

    return this.userDataSource.asObservable();
  }

  roleAddedSuccess(data: boolean): void {
    this.roleAdded.next(data);
  }

  setContentName(contentName: string): void {
    localStorage.setItem('contentName', contentName);
    this.contentName.next(contentName);
  }

  sendContentPosters(contentPosters: ContentPoster[]): void {
    if (contentPosters) {
      for (const item of contentPosters) {
        this.catchupPoster.push(item);
      }
    } else {
      this.catchupPoster = [];
    }
    // let stringArray: string[] = this.posters.map((i) => {
    //   return i.id.toString();
    // });
    // stringArray.filter((val, index, self) => {
    //   return self.indexOf(val) == index;
    // });
    // localStorage.setItem('posterIdsArray', stringArray.toString());
    this.catchupPosters.next(this.posters);
  }

  getContentPosters(): Observable<ContentPoster[]> {
    return this.contentPosters.asObservable();
  }

  setProvider(provider: boolean): void {
    this.provider.next(provider);
  }

  setLicense(license: License): void {
    //
    this.license.next(license);
  }

  getLicense(): Observable<License> {
    return this.license.asObservable();
  }

  setUtility(utilityModel: UtilityModel): void {
    if (utilityModel) {
      this.utilityModal.next(utilityModel);
    }
  }

  getUtilityModel(): Observable<UtilityModel> {
    return this.utilityModal.asObservable();
  }

  setContent(content: Content): void {
    this.content$ = content;
    this.content.next(this.content$);
  }

  getContent(): Observable<Content> {
    return this.content.asObservable();
  }

  getUserCurrentLocation(): Observable<string> {
    if (this.locationPrefix) {
      this.locationPrefix.next(localStorage.getItem('locationPrefix'));
    }

    return this.locationPrefix.asObservable();
  }

  getUrlPrefixes(): Observable<UrlPrefixes> {
    this.urlPrefixes.forEach((item) => {
      if (item.name == null || item.name == undefined) {
        item.name = localStorage.getItem('urlPrefixName');
        item.url = localStorage.getItem('urlPrefixUrl');
      }
    });
    return this.urlPrefixes.asObservable();
  }

  setContentUrlPrefix(urlPrefixes: UrlPrefixes): void {
    this.contentUrlPrefixes.next(urlPrefixes);
  }

  getContentUrlPrefix(): Observable<UrlPrefixes> {
    this.contentUrlPrefixes.forEach((item) => {
      if (item.name == null || item.name == undefined) {
        item.name = localStorage.getItem('contentUrlPrefixName');
        item.url = localStorage.getItem('contentPrefixUrl');
      }
    });

    return this.contentUrlPrefixes.asObservable();
  }

  setCategoryUrlPrefix(urlPrefixes: UrlPrefixes): void {
    this.categoryUrlPrefixes.next(urlPrefixes);
  }

  getCategoryUrlPrefix(): Observable<UrlPrefixes> {
    this.categoryUrlPrefixes.forEach((item) => {
      if (item.name == null || item.name == undefined) {
        item.name = localStorage.getItem('categoryUrlPrefixName');
        item.url = localStorage.getItem('categoryPrefixUrl');
      }
    });
    return this.categoryUrlPrefixes.asObservable();
  }

  setMenuItemUrlPrefix(urlPrefixes: UrlPrefixes): void {
    this.menuItemUrlPrefixes.next(urlPrefixes);
  }

  getMenuItemUrlPrefix(): Observable<UrlPrefixes> {
    this.menuItemUrlPrefixes.forEach((item) => {
      if (item.name == null || item.name == undefined) {
        item.name = localStorage.getItem('menuItemUrlPrefixName');
        item.url = localStorage.getItem('menuItemPrefixUrl');
      }
    });
    return this.menuItemUrlPrefixes.asObservable();
  }

  setCastUrlPrefix(urlPrefixes: UrlPrefixes): void {
    this.castUrlPrefixes.next(urlPrefixes);
  }

  getCastUrlPrefix(): Observable<UrlPrefixes> {
    this.castUrlPrefixes.forEach((item) => {
      if (item.name == null || item.name == undefined) {
        item.name = localStorage.getItem('castUrlPrefixName');
        item.url = localStorage.getItem('castPrefixUrl');
      }
    });
    return this.castUrlPrefixes.asObservable();
  }

  setCatchupUrlPrefix(urlPrefixes: UrlPrefixes): void {
    this.catchupUrlPrefixes.next(urlPrefixes);
  }

  getCatchupUrlPrefix(): Observable<UrlPrefixes> {
    this.catchupUrlPrefixes.forEach((item) => {
      if (item.name == null || item.name == undefined) {
        item.name = localStorage.getItem('catchupUrlPrefixName');
        item.url = localStorage.getItem('catchupPrefixUrl');
      }
    });
    return this.catchupUrlPrefixes.asObservable();
  }

  setContractUrlPrefix(urlPrefixes: UrlPrefixes): void {
    this.contractUrlPrefix.next(urlPrefixes);
  }

  getContractUrlPrefix(): Observable<UrlPrefixes> {
    this.contractUrlPrefix.forEach((item) => {
      if (item.name == null || item.name == undefined) {
        item.name = localStorage.getItem('contractUrlPrefixName');
        item.url = localStorage.getItem('contractPrefixUrl');
      }
    });
    return this.contractUrlPrefix.asObservable();
  }

  setReportPrefix(reportPrefix: UrlPrefixes): void {
    this.reportUrlPrefix.next(reportPrefix);
  }

  getReportUrlPrefix(): Observable<UrlPrefixes> {
    this.reportUrlPrefix.forEach((item) => {
      if (item.name == null || item.name == undefined) {
        item.name = localStorage.getItem('Report');
        item.url = localStorage.getItem('reports');
      }
    });
    return this.reportUrlPrefix.asObservable();
  }

  setEpgPrefixes(urlPrefixes: UrlPrefixes): void {
    this.epgUrlPrefix.next(urlPrefixes);
  }

  getEpgUrlPrefixes(): Observable<UrlPrefixes> {
    this.epgUrlPrefix.forEach((item) => {
      if (item.name == null || item.name == undefined) {
        item.name = localStorage.getItem('epgUrlPrefixName');
        item.url = localStorage.getItem('epgPrefixUrl');
      }
    });
    return this.epgUrlPrefix.asObservable();
  }

  setDownloadedCndFilesPrefix(cdnFilePrefix: UrlPrefixes): void {
    this.cdnFilePrefix.next(cdnFilePrefix);
  }

  getDownloadedCndFilesPrefix(): Observable<UrlPrefixes> {
    this.cdnFilePrefix.forEach((item) => {
      if (item.name == null || item.name == undefined) {
        item.name = localStorage.getItem('downloadedCdnFiles');
        item.url = localStorage.getItem('cndFilesPrefix');
      }
    });
    return this.cdnFilePrefix.asObservable();
  }

  setComingSoonUrlPrefix(urlPrefixes: UrlPrefixes): void {
    this.comingSoonUrlPrefixes.next(urlPrefixes);
  }

  setProviderUrlPrefix(urlPrefixes: UrlPrefixes): void {
    this.providerUrlPrefixes.next(urlPrefixes);
  }

  getProviderUrlPrefix(): Observable<UrlPrefixes> {
    this.providerUrlPrefixes.forEach((item) => {
      if (item.name == null || item.name == undefined) {
        item.name = localStorage.getItem('providerUrlPrefixName');
        item.url = localStorage.getItem('providerPrefixUrl');
      }
    });

    return this.providerUrlPrefixes.asObservable();
  }

  getComingSoonUrlPrefix(): Observable<UrlPrefixes> {
    this.comingSoonUrlPrefixes.forEach((item) => {
      if (item.name == null || item.name == undefined) {
        item.name = localStorage.getItem('ComingSoonUrlPrefixName');
        item.url = localStorage.getItem('ComingSoonPrefixUrl');
      }
    });
    return this.comingSoonUrlPrefixes.asObservable();
  }

  setQueryParams(queryParam: VodQueryParams): void {
    if (queryParam != null && queryParam.status == undefined) {
      queryParam.status = [];
    }
    this.queryParam.next(queryParam);
  }

  getClearBulkContents(): Observable<boolean> {
    return this.clearBulkContents.asObservable();
  }

  setClearBulkContents(clearBulkContents: boolean): void {
    this.clearBulkContents.next(clearBulkContents);
  }

  getQueryParams(): Observable<VodQueryParams> {
    return this.queryParam.asObservable();
  }

  setCastUpdate(cast: ContentCast): void {
    this.castUpdate.next(cast);
  }

  setMediaType(mediaType: string): void {
    this.mediaType.next(mediaType);
  }

  getMediaType(): Observable<string> {
    return this.mediaType.asObservable();
  }

  setOperationModalName(operationModalName: string): void {
    localStorage.setItem('process', operationModalName);
    let process = new UtilityModel();
    process.status = operationModalName;
    this.operationModalName.next(process);
  }

  getOperationModalName(): Observable<UtilityModel> {
    this.operationModalName.forEach((item) => {
      if (item.status == null || item.status == undefined) {
        item.status = localStorage.getItem('process');
      }
    });
    return this.operationModalName.asObservable();
  }

  setCatchup(catchup: Catchup): void {
    this.catchup.next(catchup);
  }

  getCatchup(): Observable<Catchup> {
    return this.catchup.asObservable();
  }

  getLanguage(): any {
    return 'tr-TR';
  }

  setCatchupPosters(posters: ContentPoster[]): void {
    if (posters) {
      this.catchupPoster.forEach((p) => {
        if (!posters.includes(p)) {
          this.catchupPoster.push(p);
        }
      });
      this.catchupPosters.next(posters);
    }
  }

  sendRefreshMessage(message: boolean): void {
    this.refreshSubject.next(message);
  }

  // CRM
  private statusDataSource = new Subject<string>();
  private channelStatusDataSource = new Subject<string>();
  private expiredDataSource = new Subject<number>();
  private qualityDataSource = new Subject<string>();
  private isCustomerDeleted = new Subject<boolean>();
  private contentGroup = new Subject<boolean>();
  private scheduleDate = new Subject<boolean>();
  private podcastContentP = new Subject<number>();
  private reEncode = new Subject<number>();
  private openEdittedMedia = new Subject<boolean>();
  private activeProduct = new Subject<boolean>();
  private label = new Subject<boolean>();
  private customerIdChange = new Subject<number>();

  refreshObservable$ = this.refreshSubject.asObservable();
  activeProduct$ = this.activeProduct.asObservable();
  customerIdChange$ = this.statusDataSource.asObservable();
  statusDataSource$ = this.statusDataSource.asObservable();
  channelStatusDataSource$ = this.channelStatusDataSource.asObservable();
  processDataSource$ = this.processDataSource.asObservable();
  expiredDataSource$ = this.expiredDataSource.asObservable();
  qualityDataSource$ = this.qualityDataSource.asObservable();
  isCustomerDeleted$ = this.isCustomerDeleted.asObservable();
  refreshStatus$ = this.refreshStatus.asObservable();
  contentGroup$ = this.contentGroup.asObservable();
  scheduleDate$ = this.scheduleDate.asObservable();
  podcastContentP$ = this.podcastContentP.asObservable();
  openEdittedMedia$ = this.openEdittedMedia.asObservable();
  reEncode$ = this.reEncode.asObservable();

  sendCustomerId(data: number) {
    this.customerIdChange.next(data);
  }

  sendStatusData(data: string) {
    this.statusDataSource.next(data);
  }

  sendExpiredData(data: number) {
    this.expiredDataSource.next(data);
  }

  sendQualityData(data: string) {
    this.qualityDataSource.next(data);
  }

  sendChannelStatusData(data: string) {
    this.channelStatusDataSource.next(data);
  }

  sendDeletedCustomer(data: boolean) {
    this.isCustomerDeleted.next(data);
  }

  sendRefresh(data: boolean) {
    this.refreshStatus.next(data);
  }

  contentGrp(data: boolean) {
    this.contentGroup.next(data);
  }

  Labels(data: boolean) {
    this.label.next(data);
  }

  scheduledate(data: boolean) {
    this.scheduleDate.next(data);
  }

  reencode(data: number) {
    this.reEncode.next(data);
  }

  sendContentP(data: number) {
    this.podcastContentP.next(data);
  }

  openEditted(data: boolean) {
    this.openEdittedMedia.next(data);
  }

  sendActiveProductDataForIndicatorChange(data: boolean) {
    this.activeProduct.next(data);
  }

  setCrmQueryParams(queryParam: CustomerQueryParams): void {
    if (queryParam != null && queryParam.status == undefined) {
      queryParam.status = [];
    }
    this.crmQueryParam.next(queryParam);
  }

  getCrmQueryParams(): Observable<CustomerQueryParams> {
    return this.crmQueryParam.asObservable();
  }
}
